import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertNumberToWords } from "./utility";

export const generateSupplierInvoicePdf = (
    invoice,
    {
        company = {},
        openInNewTab = true,
    } = {}
) => {

    const doc = new jsPDF({ unit: "mm", format: "a4" });

    /* ================== CONSTANTS ================== */
    const PAGE_W = doc.internal.pageSize.width;
    const PAGE_H = doc.internal.pageSize.height;
    const M = 20;
    const FOOTER_H = 22;

    const BRAND = {
        primary: [6, 145, 43],
        ink: [15, 23, 42],
        subtle: [100, 116, 139],
        line: [226, 232, 240],
        headerBg: [241, 245, 249],
    };

    /* ================== HELPERS ================== */
    const safe = (v, d = "-") => (v === null || v === undefined || v === "" ? d : String(v));

    const frDate = (d) =>
        d ? new Date(d).toLocaleDateString("fr-FR") : "-";

    const fmtMoney = (v) =>
        Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const amountToWords = (n) =>
        convertNumberToWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

    /* ================== HEADER ================== */
    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...BRAND.primary);
        doc.text(company.name || "MA SOCIÉTÉ", PAGE_W / 2, 22, { align: "center" });

        if (company.subtitle) {
            doc.setFontSize(10);
            doc.text(company.subtitle, PAGE_W / 2, 29, { align: "center" });
        }

        doc.setDrawColor(...BRAND.line);
        doc.line(M, 34, PAGE_W - M, 34);
    };

    /* ================== CARDS ================== */
    const CARD_W = (PAGE_W - M * 3) / 2;
    const CARD_Y = 40;

    const drawCard = (x, title, lines) => {
        const lineH = 6;
        const innerW = CARD_W - 10; // padding
        let cursorY = CARD_Y + 16;

        doc.setDrawColor(0);
        doc.roundedRect(x, CARD_Y, CARD_W, 50, 3, 3); // temp height

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND.ink);
        doc.text(title, x + 5, CARD_Y + 8);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);

        (lines || []).forEach((l) => {
            if (!l) return;

            // ✅ WRAP TEXT
            const wrapped = doc.splitTextToSize(String(l), innerW);

            doc.text(wrapped, x + 5, cursorY);
            cursorY += wrapped.length * lineH;
        });
        const cardH = Math.max(38, cursorY - CARD_Y + 4);
        return cardH;
    };

    drawHeader();

    const leftCardH = drawCard(M, "FACTURE FOURNISSEUR", [
        `N° interne : ${safe(invoice.numero)}`,
        invoice.numeroFactfournisseur
            ? `N° facture fournisseur : ${invoice.numeroFactfournisseur}`
            : null,
        `Date : ${frDate(invoice.dateFacture)}`,
        invoice.referenceBons
            ? `Bons de réception : ${invoice.referenceBons}`
            : null,
    ]);

    drawCard(M * 2 + CARD_W, "FOURNISSEUR", [
        safe(invoice.fournisseurNom),
        safe(invoice.fournisseurAdresse),
        invoice.fournisseurIce ? `ICE : ${invoice.fournisseurIce}` : null,
        invoice.fournisseurTelephone ? `Tél : ${invoice.fournisseurTelephone}` : null,
        invoice.fournisseurEmail ? `Email : ${invoice.fournisseurEmail}` : null,
    ]);

    /* ================== TABLE ================== */
    const tableY = CARD_Y + leftCardH + 12;

    autoTable(doc, {
        startY: tableY,
        head: [["Code", "Article", "Qté", "UM", "PU HT", "Total HT"]],
        body: (invoice.lignes || []).map(l => [
            safe(l.articleCode),
            safe(l.articleNom),
            safe(l.quantite),
            safe(l.unite, "U"),
            fmtMoney(l.prixUnitaireHT),
            fmtMoney(l.montantHT),
        ]),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
            fillColor: BRAND.headerBg,
            textColor: BRAND.ink,
            fontStyle: "bold",
        },
        didDrawPage: () => drawFooter(),
    });

    let y = doc.lastAutoTable.finalY + 10;

    /* ================== TOTALS ================== */
    const totalHT = invoice.montantHT || 0;
    const totalTVA = invoice.montantTVA || 0;
    const totalTTC = invoice.montantTTC || 0;

    doc.setFillColor(245, 245, 245);
    doc.roundedRect(PAGE_W - M - 70, y, 70, 40, 3, 3, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.ink);

    doc.text("Total HT", PAGE_W - M - 65, y + 10);
    doc.text(fmtMoney(totalHT), PAGE_W - M - 5, y + 10, { align: "right" });

    doc.text("TVA", PAGE_W - M - 65, y + 18);
    doc.text(fmtMoney(totalTVA), PAGE_W - M - 5, y + 18, { align: "right" });

    doc.setFont("helvetica", "bold");
    doc.text("Total TTC", PAGE_W - M - 65, y + 30);
    doc.text(fmtMoney(totalTTC), PAGE_W - M - 5, y + 30, { align: "right" });

    /* ================== AMOUNT IN WORDS ================== */
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...BRAND.subtle);
    doc.text(
        `Arrêté la présente facture fournisseur à la somme de : ${amountToWords(totalTTC)}`,
        M,
        y + 48
    );

    /* ================== FOOTER ================== */
    function drawFooter() {
        doc.setDrawColor(...BRAND.line);
        doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);

        doc.setFontSize(8);
        doc.setTextColor(80);
        doc.text(
            `${safe(company.address, "")} • ${safe(company.phone1, "")} • ${safe(company.email, "")}`,
            PAGE_W / 2,
            PAGE_H - 12,
            { align: "center" }
        );
    }

    /* ================== OUTPUT ================== */
    const fileName = `Facture-Fournisseur-${invoice.numero}.pdf`;

    if (openInNewTab) {
        window.open(doc.output("bloburl"), "_blank");
    } else {
        doc.save(fileName);
    }
};
